"use client";

import * as React from "react";
import { useTheme } from "next-themes";

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { usePartnerBranding } from "@/hooks/use-partner-branding";
import { cn } from "@/lib/utils";

export function TeamSwitcher({
  teams,
}: {
  teams: {
    name: string;
    logo: string;
  }[];
}) {
  const { state } = useSidebar();
  const { theme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const isCollapsed = state === "collapsed";
  const { isBrandingReady, partnerLogoUrl, partnerName, showPoweredBy } =
    usePartnerBranding();

  const isDarkMode = resolvedTheme === "dark" || theme === "dark";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground h-auto w-full justify-center"
        >
          <div className="flex w-full min-w-0 items-center justify-center gap-2">
            {!mounted || !isBrandingReady ? (
              <span className={isCollapsed ? "block size-8" : "block h-16 w-full"} />
            ) : partnerLogoUrl ? (
              <img
                src={partnerLogoUrl}
                alt={partnerName ?? ""}
                className={cn(
                  "object-contain",
                  isCollapsed ? "size-8" : "h-auto max-h-16 w-full min-w-0"
                )}
              />
            ) : isCollapsed ? (
              <img
                src={isDarkMode ? "/Icon_logo_dark.svg" : "/Icon_logo_light.svg"}
                alt=""
                width={40}
                height={40}
                className="size-8 object-contain"
              />
            ) : (
              <>
                <img
                  src={isDarkMode ? "/ImplementAI_main_logo_dark-cropped.svg" : "/ImplementAI_main_logo_light-cropped.svg"}
                  alt=""
                  width={120}
                  height={28}
                  className="h-auto max-h-7 min-w-0 shrink object-contain"
                />
                <img
                  src={isDarkMode ? "/Icon_logo_dark.svg" : "/Icon_logo_light.svg"}
                  alt=""
                  width={40}
                  height={40}
                  className="size-10 shrink-0 object-contain"
                />
              </>
            )}
          </div>
        </SidebarMenuButton>

        {isBrandingReady && showPoweredBy && !isCollapsed && (
          <PoweredByImplementAi isDarkMode={isDarkMode} className="-mt-2 pb-1 pr-2" />
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

interface PoweredByImplementAiProps {
  isDarkMode: boolean;
  className?: string;
}

export function PoweredByImplementAi({
  isDarkMode,
  className,
}: PoweredByImplementAiProps) {
  return (
    <div className={cn("w-full [container-type:inline-size]", className)}>
      <div className="flex w-full min-w-0 items-center justify-end gap-[0.35em] text-[clamp(5px,3.2cqw,9px)] font-semibold leading-none tracking-[-0.01em] text-muted-foreground">
        <span className="shrink-0 leading-none">Powered by</span>
        <img
          src={isDarkMode ? "/ImplementAI_main_logo_dark-cropped.svg" : "/ImplementAI_main_logo_light-cropped.svg"}
          alt="Implement AI"
          className="h-[1.15em] w-auto shrink-0 object-contain"
        />
        <img
          src={isDarkMode ? "/Icon_logo_dark.svg" : "/Icon_logo_light.svg"}
          alt=""
          className="h-[1.35em] w-auto shrink-0 object-contain"
        />
      </div>
    </div>
  );
}
